import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useEffect, useMemo, useCallback, } from 'react';
import Portal from '../Components/Utility/Portal';
import { usePopper } from 'react-popper';
import useOutsideClick from './UseOutsideClick';
import useThrottle from './UseThrottle';
export default function usePortalPopper(props) {
    const { hideOnOutsideClick = false, outsideClickCallback, outsideClickAdditionalTargets = [], isVisible: isInitiallyVisible, targetRef, popperOptions, updatePollInterval, updateOnDomMutations = false, updateOnTargetResize = true, updateOnViewportResize = true, children, render, coords, } = props;
    const [isVisible, setIsVisible] = useState(false);
    const [popperElement, setPopperElement] = useState(null);
    const virtualTarget = useMemo(() => {
        if (coords) {
            return {
                getBoundingClientRect: () => ({
                    top: coords.y,
                    left: coords.x,
                    bottom: coords.y,
                    right: coords.x,
                    width: 0,
                    height: 0,
                    x: coords.x,
                    y: coords.y,
                    toJSON: () => ({}),
                }),
            };
        }
        return null;
    }, [coords]);
    useEffect(() => {
        if (isInitiallyVisible !== undefined) {
            setIsVisible(isInitiallyVisible);
        }
    }, [isInitiallyVisible]);
    const { styles, attributes, update } = usePopper(targetRef?.current ?? virtualTarget, popperElement, popperOptions ?? {
        placement: 'bottom-start',
    });
    // The following hooks are for triggering updates to the popper position when the target moves.
    // 1. Watch for resize events on the target and the document body.
    // A resize observer can be used to detect when either of these changes size. The observation of
    // the document body should catch resizing the window.
    useEffect(() => {
        const resizeObserver = new ResizeObserver(() => {
            update?.().catch((err) => {
                throw err;
            });
        });
        // observe the target incase it resizes
        if (updateOnTargetResize && targetRef?.current) {
            resizeObserver.observe(targetRef.current);
        }
        // also observe the document body for resizes. This should cover any changes to the viewport
        if (updateOnViewportResize) {
            resizeObserver.observe(document.body);
        }
        return () => {
            resizeObserver.disconnect();
        };
    }, [targetRef, update, updateOnTargetResize, updateOnViewportResize]);
    // Create a throttled update function that only runs if the popper is visible
    const throttledUpdate = useThrottle(() => {
        if (!isVisible)
            return;
        update?.().catch((err) => {
            throw err;
        });
    }, 10);
    // 2. Watch for DOM mutations.
    // There exists scenarios where items move around the screen without the target resizing or the
    // viewport resizing. The mutation observer will detect any DOM nodes being added, removed,
    // moved, or any attributes changing and trigger an update.
    useEffect(() => {
        if (updateOnDomMutations) {
            const observer = new MutationObserver((update) => {
                // check that the mutation is not on the popper element itself
                if (update.some((mutation) => mutation.target === popperElement))
                    return;
                throttledUpdate();
            });
            observer.observe(document.body, {
                attributes: true,
                childList: true,
                subtree: true,
            });
            return () => {
                observer.disconnect();
            };
        }
    }, [updateOnDomMutations, throttledUpdate, popperElement]);
    // 3. Poll to update regularly.
    // This option is not recommended and is a last resort. Almost every scenario can be handled by
    // the mutation observer or resize observer and this option could cause performance issues. I
    // can not think of a scenario where this option is recommended but it is provided nonetheless.
    useEffect(() => {
        if (updatePollInterval === undefined || !isVisible)
            return;
        const interval = setInterval(() => {
            update?.().catch((err) => {
                throw err;
            });
        }, updatePollInterval);
        return () => {
            clearInterval(interval);
        };
    }, [updatePollInterval, update, isVisible]);
    const stableOutsideClickCallback = useCallback((e) => {
        setIsVisible(false);
        outsideClickCallback?.(e);
    }, [outsideClickCallback]);
    useOutsideClick([popperElement, targetRef?.current, ...outsideClickAdditionalTargets], stableOutsideClickCallback, hideOnOutsideClick);
    const modifiedStyles = {
        ...styles.popper,
        ...props.styles,
    };
    const renderedChildren = useMemo(() => {
        if (render !== undefined) {
            return render(setIsVisible);
        }
        return children;
    }, [render, children]);
    const portal = isVisible ? (_jsx(Portal, { ref: setPopperElement, style: modifiedStyles, ...attributes.popper, children: renderedChildren })) : null;
    return [portal, setIsVisible, isVisible, update];
}
//# sourceMappingURL=UsePortalPopper.js.map